/* RIPP 2.0 design-system primitives. Tokens live in index.css (--r2-*). */
import { useEffect, useRef, useState } from "react";

/* Smoothly interpolates a numeric value (rAF lerp) so metrics glide instead of jumping. */
export const useSmooth = (target, k = 0.12) => {
  const [v, setV] = useState(target ?? 0);
  const cur = useRef(target ?? 0);
  useEffect(() => {
    if (target == null || !Number.isFinite(Number(target))) return;
    const goal = Number(target); let raf;
    const step = () => { cur.current += (goal - cur.current) * k; if (Math.abs(goal - cur.current) < 0.0005) { cur.current = goal; setV(goal); return; } setV(cur.current); raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, k]);
  return target == null ? target : v;
};

export const SmoothNum = ({ value, format = (n) => n.toFixed(2), className = "", style, testid }) => {
  const v = useSmooth(value);
  return <span className={className} style={style} data-testid={testid}>{v == null ? "—" : format(v)}</span>;
};

export const RippPanel = ({ title, right, children, className = "", testid }) => (
  <section data-testid={testid} className={`r2-panel flex flex-col min-h-0 ${className}`}>
    {(title || right) && (
      <header className="flex items-center justify-between px-3 pt-2 pb-1.5 border-b border-[var(--r2-border)]">
        <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-[var(--r2-text2)]">{title}</span>
        {right}
      </header>
    )}
    <div className="flex-1 min-h-0 overflow-auto p-2.5">{children}</div>
  </section>
);

export const RippMetric = ({ k, v, tone = "text1", testid, small }) => (
  <div data-testid={testid}>
    <div className="text-[8px] font-mono uppercase tracking-[0.18em] text-[var(--r2-text2)]">{k}</div>
    <div className={`${small ? "text-[10.5px]" : "text-[12px]"} font-mono font-semibold tabular-nums r2-num`} style={{ color: `var(--r2-${tone})` }}>{v ?? "—"}</div>
  </div>
);

export const RippBadge = ({ children, tone = "cyan", pulse, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[8px] font-mono tracking-[0.16em] uppercase ${pulse ? "animate-pulse" : ""}`}
    style={{ color: `var(--r2-${tone})`, borderColor: `color-mix(in srgb, var(--r2-${tone}) 45%, transparent)`, background: `color-mix(in srgb, var(--r2-${tone}) 10%, transparent)` }}>
    {children}
  </span>
);

export const RippUnavailable = ({ label }) => (
  <div className="h-full grid place-items-center text-[8.5px] font-mono tracking-[0.18em] text-[var(--r2-text2)]" data-testid={`unavailable-${label}`}>
    DATA UNAVAILABLE
  </div>
);

export const RippStatusIndicator = ({ ok, label, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 text-[9px] font-mono tracking-[0.14em]" style={{ color: ok ? "var(--r2-emerald)" : "var(--r2-red)" }}>
    <span className="relative flex h-1.5 w-1.5">{ok && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />}<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" /></span>
    {label}
  </span>
);

export const RippWorkflowStage = ({ stage, active, done, tone = "cyan", last }) => (
  <div className="flex items-center gap-2 min-w-0 flex-1" data-testid={`wf-${stage.id}`} data-active={active}>
    <div className={`flex-1 min-w-0 rounded-md border px-2.5 py-2 transition-[box-shadow,border-color,opacity] duration-500 ${active ? "" : "opacity-60"}`}
      style={{ borderColor: active ? `var(--r2-${tone})` : "var(--r2-border)", boxShadow: active ? `0 0 18px color-mix(in srgb, var(--r2-${tone}) 35%, transparent)` : "none", background: "var(--r2-surface)" }}>
      <div className="text-[8.5px] font-mono uppercase tracking-[0.18em]" style={{ color: active ? `var(--r2-${tone})` : "var(--r2-text2)" }}>{stage.title}</div>
      <div className="text-[9.5px] text-[var(--r2-text1)] truncate">{stage.caption}</div>
      <div className="text-[7.5px] font-mono text-[var(--r2-text2)] truncate">{stage.agent}</div>
    </div>
    {!last && <span className={`text-[10px] ${done ? "r2-flow" : "text-[var(--r2-text2)]"}`}>»</span>}
  </div>
);

/* One plain-English line at the top of a panel: what the agent is doing right now (operator tone, readable font). */
export const NowLine = ({ text, tone = "text1", testid, className = "" }) =>
  !text ? null : (
    <div className={`r2-now ${className}`} data-testid={testid}>
      <span className="r2-now-tag">NOW</span>
      <span className="r2-now-text" style={{ color: `var(--r2-${tone})` }}>{text}</span>
    </div>
  );
